import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { getAccessToken, getClient, getCompanyId, getUid } from "@lhd/utility";
import { App, AppRecord, AppSelectionList, SearchNameType } from "@lhd/types";
import { notification } from "antd";
import { useNavigate } from "react-router-dom";
import {
  AppPartType,
  TSubmitCreatePermission,
} from "@lhd/features/Apps/AppForm/types";
import { flatMapDeep, isEmpty, pick, toNumber } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { APP_PART_TYPES } from "@lhd/features/Apps/AppForm/constants";
import { useFetchOrgs } from "./orgs";
import { useFetchPrefectures } from "./prefectures";
import {
  getCompanyPhoneNumbersOptions,
  getNameCompanyLocation,
  getOrgsData,
  getSelectionOptionsKey,
} from "@lhd/features/Apps/utils";
import axiosV2 from "@lhd/libs/axiosV2";
import dayjs from "dayjs";
import {
  useFetchCompanyLocations,
  useFetchCompanyLeaderships,
} from "./companyV2";
import { useFetchPositions } from "./positions";
import { useFetchClientsV2 } from "./clients";

export const useFetchApps = (
  page: number,
  pageSize: number,
  filterParams: any,
  allCompany = false,
) => {
  const companyId = getCompanyId();
  const uid = getUid();
  return useQuery(
    [QUERY_NAME.APPS, companyId, page, pageSize, filterParams, uid],
    () =>
      axios({
        method: "get",
        url: SERVICES.APPS,
        params: {
          page,
          per_page: pageSize,
          ...filterParams,
          current_login_company_id: companyId,
          ...(allCompany ? {} : { "q[company_id_eq]": companyId }),
        },
      }).then((res) => ({
        data: res.data as App[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 3000,
      staleTime: 3000,
    },
  );
};

export const useFetchOneApp = (
  appId: any,
  additionalParams?: Record<string, any>,
) => {
  const uid = getUid();
  const companyId = getCompanyId();
  const navigate = useNavigate();

  return useQuery(
    [QUERY_NAME.APP, appId, uid, additionalParams],
    () => {
      if (appId === undefined) return undefined;
      return axios({
        method: "get",
        url: `${SERVICES.APPS}/${appId}`,
        params: {
          current_login_company_id: companyId ? toNumber(companyId) : undefined,
          ...(additionalParams || {}),
        },
      }).then((res) => {
        const company_id = res.data.company_id;
        if (String(company_id) !== companyId) {
          notification.error({
            message:
              "このアプリは現在ログインしている会社に存在しないため、アクセスできません。",
          });
          navigate("/");
        }
        return res.data as App;
      });
    },
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      // refetchOnMount: "always",
      onError({ response }: any) {
        notification["error"]({
          key: `fetch-one-app-error-${appId}`,
          message: response?.data?.error || response?.data?.msg,
        });
      },
      enabled: !!appId,
    },
  );
};

export const useDeleteApp = (appId: string) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const companyId = getCompanyId();
  return useMutation(
    () =>
      axios({
        method: "delete",
        url: `${SERVICES.APPS}/${appId}`,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        notification["info"]({
          message: "削除が完了しました",
        });
        navigate(SERVICES.APPS);
      },
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.message,
        });
      },
    },
  );
};

export const useSubmitApp = (options?: { isIgnoreReload?: boolean }) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload, id }: any) =>
      axios({
        method: id ? "PUT" : "POST",
        url: `${SERVICES.APPS}${id ? `/${id}` : ""}`,
        data: payload,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    {
      onSuccess() {
        if (!options?.isIgnoreReload) {
          queryClient.invalidateQueries([QUERY_NAME.FETCH_RECORD]);
          queryClient.invalidateQueries([QUERY_NAME.APP]);
        }
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useSubmitAppToStore = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ listAppIds }: { listAppIds: number[] }) => {
      const formData = new FormData();
      listAppIds.forEach((id: number) => {
        formData.append(`app_ids[][id]`, String(id));
      });
      return axios({
        method: "POST",
        url: `${SERVICES.APPS}/submit-store`,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        queryClient.invalidateQueries([QUERY_NAME.APP]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useCancelSubmitAppToStore = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ listAppIds }: { listAppIds: number[] }) => {
      const formData = new FormData();
      listAppIds.forEach((id: number) => {
        formData.append(`app_ids[][id]`, String(id));
      });
      return axios({
        method: "POST",
        url: `${SERVICES.APPS}/cancel-submit-store`,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        queryClient.invalidateQueries([QUERY_NAME.APP]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useEditApps = (appId: string | undefined) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APPS}/${appId}/${SERVICES.APPS_CREATE}`,
        data: payload,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        queryClient.invalidateQueries([QUERY_NAME.FETCH_RECORD]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDuplicateRecord = (appId: string) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    (recordId: string) =>
      axios({
        method: "post",
        url: `${SERVICES.APPS}/${appId}/records/${recordId}/duplicate`,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        queryClient.invalidateQueries([QUERY_NAME.FETCH_RECORD]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError() {
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        queryClient.invalidateQueries([QUERY_NAME.FETCH_RECORD]);
        notification["error"]({
          message: "アプリのレコードが存在しません",
        });

        return;
      },
    },
  );
};

export const useFetchRecord = (appId: string | undefined, query?: string) => {
  const uid = getUid();
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.FETCH_RECORD, appId, query, uid, companyId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPS}/${appId}${SERVICES.APPS_RECORDS}${
          query ? `${query}` : ""
        }`,
        params: {
          company_id: companyId,
        },
      }).then((res) => {
        return {
          data: res.data as AppRecord[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      cacheTime: 0,
      staleTime: Infinity,
      enabled: !!appId,
      retry: false,
      onError({ response }: any) {
        notification["error"]({
          message:
            response?.data?.msg ||
            "レコードが削除されました。もう一度共有側で確認してください。",
        });
      },
    },
  );
};

export const useFetchRecordWithoutParams = (
  appId: string | undefined,
  query?: string,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.FETCH_RECORD, appId, companyId, query],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPS}/${appId}${SERVICES.APPS_RECORDS}${
          query ? `${query}` : ""
        }`,
        params: {
          company_id: companyId,
        },
      }).then((res) => {
        return {
          data: res.data as AppRecord[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      cacheTime: 0,
      staleTime: Infinity,
      enabled: !!appId,
      retry: 1,
    },
  );
};

export const useSaveRecord = (appId: string) => {
  const companyId = getCompanyId();
  return useMutation(
    ({ data, cb }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APPS}/${appId}${SERVICES.SAVE_RECORDS}`,
        data,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess(res, variables) {
        variables.cb && variables.cb();
        if (isEmpty(res?.data?.errors)) {
          notification["success"]({
            message: "保存しました",
          });
        } else {
          notification["error"]({
            message: "保存失敗しました",
          });
        }
      },
    },
  );
};

export const useUpdateRecord = (appId: string, shouldNotRefetch?: boolean) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    ({ payload, submitId }: any) =>
      axios({
        method: "patch",
        url: `${SERVICES.APPS}/${appId}${SERVICES.APPS_RECORDS}/${submitId}`,
        data: payload,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess(data: any, variables: any) {
        if (variables.cb) {
          variables.cb();
        }
        if (!shouldNotRefetch) {
          queryClient.invalidateQueries([QUERY_NAME.APPS]);
          queryClient.invalidateQueries([QUERY_NAME.FETCH_RECORD, appId]);
        } else {
          queryClient.setQueryData([QUERY_NAME.FETCH_RECORD, appId], () => {
            return {
              data: [data?.data],
            };
          });
        }
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(_: any, variables: any) {
        variables.onError && variables.onError();
      },
    },
  );
};

export const useDeleteRecord = (appId: string) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    ({ submitId }: any) =>
      axios({
        method: "delete",
        url: `${SERVICES.APPS}/${appId}${SERVICES.APPS_RECORDS}/${submitId}`,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        queryClient.invalidateQueries([QUERY_NAME.FETCH_RECORD]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDeleteMultipleRecords = (appId: string) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    ({ listWorkerIds }: any) => {
      const formData = new FormData();
      listWorkerIds.forEach((id: any) => {
        formData.append(`delete_ids[]`, id);
      });
      formData.append("company_id", companyId);
      return axios({
        method: "post",
        data: formData,
        url: `${SERVICES.APPS}/${appId}${SERVICES.APPS_RECORDS}/delete_multiple`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(data, variables) {
        if (variables.cb) variables.cb();
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        queryClient.invalidateQueries([QUERY_NAME.FETCH_RECORD]);
        const numOfNotify = document.getElementsByClassName(
          "ant-notification-notice-message",
        )?.length;
        if (numOfNotify === 0) {
          notification["success"]({
            message: "保存しました",
          });
        }
      },
    },
  );
};

export const useCreatePermission = () => {
  return useMutation(
    ({ payload }: { payload: TSubmitCreatePermission }) =>
      axios({
        method: "post",
        url: `${SERVICES.PERMISSIONS}`,
        data: payload,
      }),
    {
      onSuccess() {},
    },
  );
};

export const useUpdatePermission = () => {
  return useMutation(
    ({ payload }: { payload: TSubmitCreatePermission }) =>
      axios({
        method: "patch",
        url: `${SERVICES.APPS}/${SERVICES.PERMISSIONS}/${payload?.id}`,
        data: payload,
      }),
    {
      onSuccess() {},
    },
  );
};

export const useDeletePermission = () => {
  return useMutation(
    ({ payload }: { payload: TSubmitCreatePermission }) =>
      axios({
        method: "delete",
        url: `${SERVICES.APPS}/${SERVICES.PERMISSIONS}/${payload?.id}`,
      }),
    {
      onSuccess() {},
    },
  );
};

export const useSubmitAppTree = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APPS}/tree`,
        data: payload,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDuplicateApp = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ appId, typeCopy }: { appId: string; typeCopy: string }) =>
      axios({
        method: "post",
        url: `${SERVICES.APPS}/${appId}/duplicate`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
        data: {
          company_id: companyId,
          copy_type: typeCopy,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useApproveApp = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ appId }: { appId: string }) =>
      axios({
        method: "post",
        url: `${SERVICES.APPS}/${appId}/approved`,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useGetListAppNames = (filterParams: SearchNameType) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.APPS, companyId, filterParams],
    () =>
      axios({
        method: "get",
        url: SERVICES.APPS,
        params: {
          page: 1,
          per_page: 9999,
          ...filterParams,
          current_login_company_id: companyId,
          "q[company_id_eq]": companyId,
          expose_attributes: ["id", "name", "company_id", "children"],
        },
      }).then((res) => ({
        data: res.data as App[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 30000,
      staleTime: 30000,
    },
  );
};
export const useGetListTextFields = (
  id: string,
  filterParams: SearchNameType,
  enabled: boolean,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.APP_PARTS, companyId, id, filterParams],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APP_PART}?&q[app_id_eq]=${id}&q[app_part_type_eq]=text`,
        params: {
          ...filterParams,
        },
      }).then((res) => ({
        data: res.data as AppPartType[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 30000,
      staleTime: 30000,
      enabled,
    },
  );
};

export const useSubmitShareRecordConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ appId, body }: { appId: string; body: any }) => {
      if (!appId) {
        return Promise.reject();
      }

      return axios({
        method: "post",
        url: `${SERVICES.APPS}/${appId}/records/update_share_record`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: body,
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPS]);
        notification["success"]({
          message: "コピー送信が正常に完了しました。",
        });
      },
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.message || "コピー送信に失敗しました。",
        });
      },
    },
  );
};

export const useFetchWorkersLessFields = (params: {
  companyId?: number | string;
  enabled?: boolean;
}) => {
  const companyId = Number(params.companyId || getCompanyId());
  return useQuery(
    [QUERY_NAME.WORKERS, "list_less_fields", companyId],
    async () => {
      const rs = await axiosV2({
        method: "get",
        url: SERVICES.WORKERS + "/list_less_fields",
        params: {
          page: 1,
          per_page: 10000,
          "q[company_id_eq]": companyId,
          company_id: companyId,
        },
      });
      return rs.data;
    },
    {
      cacheTime: Infinity,
      enabled: params.enabled,
      staleTime: Infinity,
    },
  );
};

export const useGetSelectOptionByAppPartType = (
  appParts: AppPartType[],
  paramsCompanyId?: number | string,
) => {
  const [selectionDataList, setSelectionDataList] = useState<AppSelectionList>({
    branch_selection: undefined,
    department_selection: undefined,
    division_selection: undefined,
    worker_selection: undefined,
    organization_selection: undefined,
    company_location_selection: undefined,
    representative_selection: undefined,
    company_phone_number_selection: undefined,
    position_selection: undefined,
    customer_selection: undefined,
  });
  const companyId = Number(paramsCompanyId || getCompanyId());
  const { data: listOrg } = useFetchOrgs(undefined, undefined, undefined);

  const listSelectionNeedCall = useMemo(() => {
    const result: any = {};
    const getListSelectionNeedCall = (app_parts: AppPartType[]) => {
      app_parts?.forEach((app_part) => {
        const optionKey = getSelectionOptionsKey(app_part.app_part_type);
        if (
          optionKey &&
          Object.prototype.hasOwnProperty.call(selectionDataList, optionKey)
        ) {
          result[optionKey] = true;
        } else if (app_part.app_part_type === APP_PART_TYPES.TABLE) {
          getListSelectionNeedCall(app_part?.children || []);
        }
      });
    };

    getListSelectionNeedCall(appParts || []);
    return result;
  }, [appParts]);

  const flattenDeepArray: any = useCallback((originalArray: any) => {
    return flatMapDeep(originalArray, (item) => {
      const transformedItem = pick(item, ["id", "name"]);
      const children = item.children || [];

      return [transformedItem, ...flattenDeepArray(children)];
    });
  }, []);

  const getDataByOrgType = async (type: string) => {
    const listId =
      listOrg?.data
        ?.filter((org) => org.org_type === type)
        ?.map((orgItem) => orgItem?.id) ?? [];

    if (!listId.length) return undefined;

    const arrData = await Promise.all(
      listId.map(async (id) => await getOrgsData(id, companyId)),
    );

    const mergedArray = arrData.reduce(
      (prevArr, arrItem) => [...prevArr, ...arrItem],
      [],
    );

    return flattenDeepArray(mergedArray);
  };

  const { data: workersData } = useFetchWorkersLessFields({
    companyId: companyId,
    enabled:
      listSelectionNeedCall.worker_selection ||
      listSelectionNeedCall.representative_selection,
  });

  const { data: companyLocationsData } = useFetchCompanyLocations(
    listSelectionNeedCall.company_location_selection ? companyId : 0,
    1,
    1000,
    "",
  );

  const { data: companyLeadershipsData } = useFetchCompanyLeaderships(
    listSelectionNeedCall.representative_selection ? companyId : 0,
    1,
    1000,
  );

  const { data: prefecturesData } = useFetchPrefectures();

  const { data: positionsData } = useFetchPositions(1, 1000, {});
  const { data: clientsData } = useFetchClientsV2(
    1,
    1000,
    "registration_information",
    {},
  );

  const getSelectionData = async () => {
    if (isEmpty(listSelectionNeedCall)) return;

    const list = { ...selectionDataList };

    let hasChanged = false;
    if (
      workersData &&
      list.worker_selection === undefined &&
      listSelectionNeedCall.worker_selection
    ) {
      list.worker_selection = workersData;
      hasChanged = true;
    }

    if (
      companyLocationsData &&
      list.company_location_selection === undefined &&
      listSelectionNeedCall.company_location_selection
    ) {
      const activeLocations = (companyLocationsData.data || []).map(
        (loc: any) => {
          return {
            id: loc.id,
            name: getNameCompanyLocation(loc, prefecturesData) || "未設定",
          };
        },
      );
      list.company_location_selection = activeLocations;
      hasChanged = true;
    }

    if (
      companyLeadershipsData &&
      list.representative_selection === undefined &&
      listSelectionNeedCall.representative_selection
    ) {
      const activeLeaderships = companyLeadershipsData.data || [];
      const repOptions = activeLeaderships.map((lead: any) => {
        const worker = (workersData || []).find(
          (w: any) => Number(w.id) === Number(lead.worker_id),
        );
        const officerName = worker
          ? (worker.first_name && worker.last_name
              ? `${worker.first_name} ${worker.last_name}`
              : worker.full_name || worker.name) + ` - ${worker?.worker_no}`
          : `Worker #${lead.worker_id}`;
        return {
          id: lead.worker_id,
          name: officerName,
          full_name: officerName,
          first_name: worker?.first_name || "",
          last_name: worker?.last_name || "",
        };
      });
      list.representative_selection = repOptions;
      hasChanged = true;
    }

    if (
      list.company_phone_number_selection === undefined &&
      listSelectionNeedCall.company_phone_number_selection
    ) {
      try {
        list.company_phone_number_selection =
          await getCompanyPhoneNumbersOptions(companyId);
      } catch (e) {
        list.company_phone_number_selection = [];
      }
      hasChanged = true;
    }

    if (
      positionsData?.data &&
      list.position_selection === undefined &&
      listSelectionNeedCall.position_selection
    ) {
      list.position_selection = (positionsData.data || []).map((pos: any) => ({
        id: pos.id,
        name: pos.name,
      }));
      hasChanged = true;
    }

    if (
      clientsData?.data &&
      list.customer_selection === undefined &&
      listSelectionNeedCall.customer_selection
    ) {
      list.customer_selection = (clientsData.data || []).map((client: any) => ({
        id: client.id,
        name: client?.client_name?.name || "",
      }));
      hasChanged = true;
    }

    const orgFetchConfig: { key: keyof AppSelectionList; orgType: string }[] = [
      { key: "division_selection", orgType: "division" },
      { key: "organization_selection", orgType: "chairman" },
      { key: "branch_selection", orgType: "branch" },
      { key: "department_selection", orgType: "department" },
    ];

    const orgFetchTasks = listOrg
      ? orgFetchConfig.filter(
          ({ key }) => listSelectionNeedCall[key] && list[key] === undefined,
        )
      : [];

    if (orgFetchTasks.length > 0) {
      const results = await Promise.allSettled(
        orgFetchTasks.map(({ orgType }) => getDataByOrgType(orgType)),
      );

      results.forEach((result, index) => {
        const key = orgFetchTasks[index].key;
        if (result.status === "fulfilled") {
          list[key] = result.value ?? [];
        } else {
          list[key] = [];
        }
        hasChanged = true;
      });
    }

    if (hasChanged) {
      setSelectionDataList(list);
    }
  };

  useEffect(() => {
    getSelectionData();
  }, [
    listSelectionNeedCall,
    listOrg?.data,
    workersData,
    companyLocationsData,
    companyLeadershipsData,
    prefecturesData,
    positionsData,
    clientsData,
  ]);

  return selectionDataList;
};

export const useFetchEmployeeInfoChildrenSelection = ({
  url,
  params,
  enabled = true,
}: {
  url: string;
  params?: Record<string, any>;
  enabled?: boolean;
}) => {
  return useQuery(
    [QUERY_NAME.EMPLOYEE_INFO_CHILDREN_SELECTION, url, params],
    async () => {
      const rs = await axiosV2({
        method: "get",
        url,
        params,
      });
      return rs.data;
    },
    {
      enabled: !!enabled && !!url,
    },
  );
};

export const useFetchFilterOptionsV1 = (params: {
  appId: string;
  data: {
    field_key: string;
    active_filters: any;
    filter_order: string[];
    filter_edit_records?: boolean;
  };
  enabled: boolean;
  companyId?: number | string;
}) => {
  const queryClient = useQueryClient();
  const { appId, data, enabled = true, companyId = getCompanyId() } = params;
  const queryKey = [
    QUERY_NAME.FETCH_RECORD,
    "apps",
    appId,
    "filter_options_v1",
    data,
    companyId,
  ];

  return useQuery(
    queryKey,
    async () => {
      const url = `${SERVICES.APPS}/${appId}${SERVICES.APPS_RECORDS}/filter_options`;
      const payloadData: any = {
        ...data,
        company_id: companyId,
      };

      const res = await axios({
        method: "post",
        url,
        data: payloadData,
      });

      return {
        [data.field_key]: res.data?.filter_options?.[data.field_key] || [],
      };
    },
    {
      enabled,
      keepPreviousData: true,
      retry: false,
      onSuccess(data) {
        queryClient.setQueryData(queryKey, data);
      },
    },
  );
};
